import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import type { Connection } from '../lib/connection';
import { clickReport, keySeq, promptInput, scrollHistory, wheelReport, type KeyName } from '../lib/xtermInput';
import { readMenu, type MenuItem } from '../lib/menu';
import { bufferText } from '../lib/termText';
import { getTelegramWebApp } from '../lib/telegram';

const decoder = new TextDecoder();
/** Vertical travel per reported wheel step. Roughly a finger-flick per line. */
const SWIPE_PX_PER_LINE = 24;
/** Beyond this a touch is a swipe, not a tap — stops scrolls registering as clicks. */
const TAP_SLOP_PX = 10;
/** A held finger means "I want this text", not "click here". Matches the delay every
 *  platform uses for its own selection handles, so it feels like the gesture that
 *  failed here before rather than a new one to learn. */
const LONG_PRESS_MS = 450;

export interface TermHandle {
  sendKey(key: KeyName): void;
  sendText(text: string): void;
  /** What is on screen, as plain text. The terminal is the only thing that knows. */
  text(): string;
}

/**
 * `disableStdin: true` is the single most important line here.
 *
 * xterm captures keystrokes through a hidden textarea, which on iOS fights the
 * software keyboard: the caret jumps, autocorrect rewrites control sequences, and
 * the viewport scrolls out from under the cursor. Routing input through the composer
 * and key bar instead sidesteps that entire class of bug.
 *
 * Mouse reports are synthesised when a running application requests them.
 * With Codex's default --no-alt-screen, swipes use the terminal's real scrollback.
 */
export const Term = forwardRef<
  TermHandle,
  {
    sid: string;
    conn: Connection;
    fontSize: number;
    onMenu?: (items: MenuItem[]) => void;
    /** Fired on a long press. Selecting text in the terminal itself is impossible —
     *  see TermText — so the gesture opens the sheet that can do it. */
    onSelectText?: () => void;
  }
>(function Term({ sid, conn, fontSize, onMenu, onSelectText }, ref) {
    const hostRef = useRef<HTMLDivElement>(null);
    const termRef = useRef<Terminal | null>(null);
    const fitRef = useRef<FitAddon | null>(null);

    useImperativeHandle(ref, () => ({
      sendKey(key: KeyName) {
        const term = termRef.current;
        if (!term) return;
        if (scrollHistory(term, key)) return;
        send(keySeq(term, key));
      },
      sendText(text: string) {
        const term = termRef.current;
        if (term) send(promptInput(term, text));
      },
      text() {
        const term = termRef.current;
        return term ? bufferText(term) : '';
      },
    }));

    const send = (data: string) => {
      conn.send({ t: 'c.input', sid, b64: btoa(String.fromCharCode(...new TextEncoder().encode(data))) });
    };

    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;

      const term = new Terminal({
        disableStdin: true,
        cursorBlink: false,
        convertEol: false,
        fontSize,
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
        lineHeight: 1.15,
        scrollback: 5000,
        theme: { background: '#000000', foreground: '#e6e6e6' },
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(host);
      fit.fit();

      termRef.current = term;
      fitRef.current = fit;
      conn.send({ t: 'c.attach', sid, cols: term.cols, rows: term.rows });

      /** Pixel → cell. The screen element is exactly the character grid, so its box
       *  divided by cols/rows gives cell size without reaching into xterm internals. */
      const cellAt = (clientX: number, clientY: number): { col: number; row: number } => {
        const screen = host.querySelector('.xterm-screen') as HTMLElement | null;
        const rect = (screen ?? host).getBoundingClientRect();
        const col = Math.floor(((clientX - rect.left) / rect.width) * term.cols);
        const row = Math.floor(((clientY - rect.top) / rect.height) * term.rows);
        return {
          col: Math.min(term.cols - 1, Math.max(0, col)),
          row: Math.min(term.rows - 1, Math.max(0, row)),
        };
      };

      const mouseOn = () => term.modes.mouseTrackingMode !== 'none';

      const onWheel = (e: WheelEvent) => {
        if (!mouseOn()) return;
        e.preventDefault();
        e.stopPropagation();
        const { col, row } = cellAt(e.clientX, e.clientY);
        const steps = Math.min(5, Math.max(1, Math.round(Math.abs(e.deltaY) / SWIPE_PX_PER_LINE)));
        send(wheelReport(e.deltaY < 0, col, row).repeat(steps));
      };

      let touch: { x: number; y: number; lastY: number; moved: number } | null = null;
      let holdTimer: number | undefined;

      /** Ends the gesture without letting it become a tap: dropping `touch` is what
       *  stops touchend from firing a click at wherever the finger was resting. */
      const cancelTouch = (e?: Event) => {
        e?.stopPropagation();
        window.clearTimeout(holdTimer);
        touch = null;
      };

      const onTouchStart = (e: TouchEvent) => {
        e.stopPropagation();
        const t = e.touches[0];
        if (!t) return;
        touch = { x: t.clientX, y: t.clientY, lastY: t.clientY, moved: 0 };
        window.clearTimeout(holdTimer);
        holdTimer = window.setTimeout(() => {
          cancelTouch();
          getTelegramWebApp()?.HapticFeedback?.impactOccurred('medium');
          onSelectText?.();
        }, LONG_PRESS_MS);
      };

      const onTouchMove = (e: TouchEvent) => {
        e.stopPropagation();
        if (e.cancelable) e.preventDefault();
        const t = e.touches[0];
        if (!t || !touch) return;
        // A finger that travels is scrolling, whatever it meant a moment ago. Checked
        // before the mouse-mode guard below, so the hold is cancelled even in a plain
        // shell, where none of the reporting underneath it runs at all.
        if (Math.abs(t.clientX - touch.x) > TAP_SLOP_PX || Math.abs(t.clientY - touch.y) > TAP_SLOP_PX) {
          window.clearTimeout(holdTimer);
        }
        touch.moved += Math.abs(t.clientY - touch.lastY);

        const dy = t.clientY - touch.lastY;
        const steps = Math.trunc(Math.abs(dy) / SWIPE_PX_PER_LINE);
        if (steps < 1) return;
        e.preventDefault();
        const lines = Math.min(steps, 5);
        touch.lastY = t.clientY;

        // Two different worlds, and the mode is the only way to tell them apart. A TUI
        // that asked for mouse reports (Codex, htop) owns the gesture and gets it
        // encoded; a plain shell has real scrollback instead, and there the swipe has to
        // move the viewport ourselves — `touch-action: none` in styles.css means the
        // browser will not, and without this a finger over a shell does nothing at all.
        if (!mouseOn()) {
          // Drag down to see earlier output, same direction either way.
          term.scrollLines(dy > 0 ? -lines : lines);
          return;
        }

        const { col, row } = cellAt(t.clientX, t.clientY);
        send(wheelReport(dy > 0, col, row).repeat(lines));
      };

      const onTouchEnd = (e: TouchEvent) => {
        e.stopPropagation();
        const start = touch;
        cancelTouch();
        if (!start || !mouseOn()) return;
        if (start.moved > TAP_SLOP_PX) return; // that was a scroll
        const t = e.changedTouches[0];
        if (!t) return;
        const { col, row } = cellAt(t.clientX, t.clientY);
        send(clickReport(col, row));
      };

      // Own gestures before xterm's handlers: otherwise its viewport scrolls first,
      // then our bubbling handler changes the same scroll position a second time.
      // A wheel without mouse tracking still reaches xterm's native scrollback.
      host.addEventListener('wheel', onWheel, { capture: true, passive: false });
      host.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
      host.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
      host.addEventListener('touchend', onTouchEnd, { capture: true, passive: true });
      // Telegram or the OS can take the gesture away mid-hold; without this the timer
      // would fire the sheet open long after the finger was gone.
      host.addEventListener('touchcancel', cancelTouch, { capture: true, passive: true });

      // Debounced: Codex repaints in bursts, and scanning mid-burst sees a half-drawn
      // menu. 250ms of quiet means it has finished drawing whatever it drew.
      let menuTimer: number | undefined;
      const scanMenu = () => {
        window.clearTimeout(menuTimer);
        menuTimer = window.setTimeout(() => onMenu?.(readMenu(term)), 250);
      };

      const offSnap = conn.on('snapshot', (s, data) => {
        if (s !== sid) return;
        // A snapshot is the whole screen, not a delta — painting it over old content
        // would double every line the mirror already had.
        term.reset();
        term.write(decoder.decode(data));
        scanMenu();
      });
      const offData = conn.on('data', (s, data) => {
        if (s !== sid) return;
        term.write(decoder.decode(data));
        scanMenu();
      });

      const ro = new ResizeObserver(() => {
        try {
          fit.fit();
          conn.send({ t: 'c.resize', sid, cols: term.cols, rows: term.rows });
        } catch {
          /* fires once while the element is still 0×0 during mount */
        }
      });
      ro.observe(host);

      return () => {
        window.clearTimeout(menuTimer);
        window.clearTimeout(holdTimer);
        offSnap();
        offData();
        ro.disconnect();
        host.removeEventListener('wheel', onWheel, true);
        host.removeEventListener('touchstart', onTouchStart, true);
        host.removeEventListener('touchmove', onTouchMove, true);
        host.removeEventListener('touchend', onTouchEnd, true);
        host.removeEventListener('touchcancel', cancelTouch, true);
        conn.send({ t: 'c.detach', sid });
        term.dispose();
        termRef.current = null;
      };
    }, [sid, conn, onMenu, onSelectText]);

    useEffect(() => {
      const term = termRef.current;
      if (!term) return;
      term.options.fontSize = fontSize;
      try {
        fitRef.current?.fit();
        conn.send({ t: 'c.resize', sid, cols: term.cols, rows: term.rows });
      } catch {
        /* ignore */
      }
    }, [fontSize, sid, conn]);

  return <div className="term-host" ref={hostRef} />;
});
